import { AvailableDateTimeFormats, makeDateTimeFormattersForTimezone } from '@aytm/app/insights/dashboard/lib/datetime'
import { measurementMapReducer } from './measurements'
import React, { createContext, useState, useRef, useMemo, useReducer, useCallback } from 'react'

export interface HidableAPI {
  hide(): void
}

export type UIGeneralContextType = {
  hoverHandlers: Record<string | number, Record<string | number, { node?: HTMLElement }>>
  hoverState: Record<string | number, boolean>
  updateHoverState: React.Dispatch<React.SetStateAction<Record<string | number, boolean>>>
  lastHidableRef?: React.MutableRefObject<HidableAPI>
  currentFormatters: Record<AvailableDateTimeFormats[number], Intl.DateTimeFormat>

  timeZoneTitle: string
  setTimeZoneTitle: React.Dispatch<React.SetStateAction<string>>
  timeZoneOffset: string
  setTimeZoneOffset: React.Dispatch<React.SetStateAction<string>>
  measurements: Map<string, number>
  setMeasurements: (key: string, value: number) => void
}

export const UIGeneralContext = createContext<UIGeneralContextType>(null)

export const UIGeneralProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const hoverHandlersRef = React.useRef<UIGeneralContextType['hoverHandlers']>(null)
  if (null === hoverHandlersRef.current) {
    hoverHandlersRef.current = {} as UIGeneralContextType['hoverHandlers']
  }
  const hoverStateRef = React.useRef<UIGeneralContextType['hoverState']>(null)
  if (null === hoverStateRef.current) {
    hoverStateRef.current = {} as UIGeneralContextType['hoverState']
  }

  const [hoverState, updateHoverState] = useState<UIGeneralContextType['hoverState']>({})

  const lastHidableRef = useRef<HidableAPI>(null)

  const [timeZoneTitle, setTimeZoneTitle] = useState<string | null>(null)
  const [timeZoneOffset, setTimeZoneOffset] = useState<string | null>(null)

  const currentFormatters = useMemo(() => makeDateTimeFormattersForTimezone(timeZoneOffset ?? 'UTC'), [timeZoneOffset])

  const [measurements, dispatch] = useReducer(measurementMapReducer, new Map<string, number>())
  const setMeasurements = useCallback((key: string, value: number) => {
    dispatch({ type: 'set', key, value })
  }, [])

  const contextValue = useMemo(() => {
    return {
      hoverHandlers: hoverHandlersRef.current,
      hoverState,
      updateHoverState,
      lastHidableRef,
      timeZoneTitle,
      setTimeZoneTitle,
      timeZoneOffset,
      setTimeZoneOffset,
      currentFormatters,
      measurements,
      setMeasurements
    }
  }, [currentFormatters, hoverState, timeZoneOffset, timeZoneTitle, measurements, setMeasurements])

  return <UIGeneralContext.Provider value={contextValue}>{children}</UIGeneralContext.Provider>
}

export const useUIGeneralContext = (): UIGeneralContextType => {
  const context = React.useContext(UIGeneralContext)
  if (null === context) {
    throw new Error('useUIGeneralContext must be used within a <UIGeneralProvider>')
  }
  return context
}
export const useMeasurementContext = () => {
  const context = React.useContext(UIGeneralContext)
  if (!context) {
    throw new Error('useMeasurementContext must be used within a MeasurementProvider')
  }
  const { measurements, setMeasurements } = context
  return { measurements, setMeasurements } as const
}
